import { ChangeEvent, useEffect, useState } from 'react';

import {
  alpha,
  Box,
  Divider,
  ListItemText,
  makeStyles,
  Tab,
  Tabs,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { usePhoneInput } from 'react-international-phone';

import { BlockedContactBadge } from 'components/BlockedContactBadge';
import { BlockedContactName } from 'components/BlockedContactName';
import { ContactAvatar } from 'components/ContactAvatar';
import ImageViewer from 'components/ImageViewer';
import TabPanel from 'components/TabPanel';
import { useAppDispatch, useAppSelector } from 'store';
import {
  AtendimentoInterfaceV2,
  atendimentosSelector,
} from 'store/reducers/atendimentos';
import {
  contatosSelector,
  ContatoInterfaceV2,
  fetchContatoAtendimentoV2,
} from 'store/reducers/contatos';
import getIconTipoCanal from 'utils/getIconTipoCanal';

import TabPanelArquivos from '../TabPanelArquivos';
import TabPanelAtendimento from '../TabPanelAtendimento';
import MessagesGridItem from './MessagesGridItem';

const useStyles = makeStyles(
  ({ typography, spacing, palette, breakpoints }) => ({
    actionButton: {
      borderRadius: spacing(10),
      fontSize: typography.body2.fontSize,
      fontWeight: typography.fontWeightMedium as number,
      [breakpoints.down('sm')]: {
        fontSize: typography.caption.fontSize,
        padding: spacing(0.75, 1.5),
      },
    },
    tab: {
      borderTop: `1px solid ${palette.divider}`,
      '& .MuiTab-root': {
        minWidth: 'auto',
        [breakpoints.down('sm')]: {
          minWidth: 60,
          padding: spacing(1, 0.5),
          fontSize: typography.caption.fontSize,
        },
      },
    },
    tabPanel: {
      height: '100%',
      padding: spacing(3),
      overflowY: 'auto',
      overflowX: 'hidden',
      [breakpoints.down('sm')]: {
        padding: spacing(2),
      },
    },
    headerBox: {
      display: 'flex',
      flexDirection: 'column',
      gridGap: 24,
      padding: spacing(3, 3, 2),
      [breakpoints.down('sm')]: {
        padding: spacing(2, 2, 1.5),
        gridGap: 16,
      },
    },
    contactInfo: {
      width: '100%',
      padding: spacing(1),
      backgroundColor: alpha(palette.background.tertiary, 0.5),
      display: 'flex',
      alignItems: 'center',
      gap: spacing(2),
      borderRadius: spacing(1),
    },
  }),
);

interface LeftGridDetalhesContatoProps {
  contatoId: string;
  initialLoadingMessages: boolean;
  onReset: () => void;
}

export default function LeftGridDetalhesContato({
  contatoId,
  initialLoadingMessages,
  onReset,
}: LeftGridDetalhesContatoProps) {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const { historicoAtendimentoSelecionado } =
    useAppSelector(atendimentosSelector);
  const atendimento = historicoAtendimentoSelecionado as AtendimentoInterfaceV2;

  const [tabValue, setTabValue] = useState(0);
  const { contatoAtendimentoV2: contato } = useAppSelector(contatosSelector);
  const contatoV2 = contato as ContatoInterfaceV2;

  // Em tela grande o grid direito exibe as mensagens, então
  // "Arquivos" fica no índice 1. Em tela pequena, "Histórico"
  // é inserido no índice 1 e "Arquivos" passa para o índice 2.
  const arquivosIndex = isSmallScreen ? 2 : 1;

  const { phone } = usePhoneInput({
    defaultCountry: 'br',
    value: contato?.contact ?? '',
  });

  const displayPhone = contato?.contact && phone ? phone : contato?.name;
  const IconTipoCanal = contato
    ? getIconTipoCanal({
        tipoCanal: contato?.tipoCanal,
        size: 14,
        isModoHibrido: contato.channel?.isHybridMode,
      })
    : null;

  const handleTabChange = (
    event: ChangeEvent<Record<string, unknown>>,
    newValue: number,
  ) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    setTabValue(0);

    dispatch(fetchContatoAtendimentoV2(contatoId));
  }, [atendimento, contatoId, dispatch]);

  return (
    <Box height="100%" display="flex" flexDirection="column">
      <Box className={classes.headerBox}>
        <Box width="100%" display="flex" justifyContent="center">
          <Box
            display="flex"
            alignItems="center"
            className={classes.contactInfo}
          >
            <Box
              style={{
                position: 'relative',
                display: 'inline-flex',
                marginBottom: 20,
              }}
            >
              <ImageViewer src={contato?.fotoUrl}>
                <BlockedContactBadge isBlocked={Boolean(contato?.blockedAt)}>
                  <ContactAvatar src={contato?.fotoUrl} />
                </BlockedContactBadge>
              </ImageViewer>
            </Box>

            <ListItemText
              primary={
                <BlockedContactName
                  name={contato?.name ?? ''}
                  isBlocked={Boolean(contato?.blockedAt)}
                  variant="body2"
                  style={{ fontSize: 13, fontWeight: 500 }}
                />
              }
              secondary={
                <Box display="flex" flexDirection="column" gridGap={4}>
                  <Box
                    display="flex"
                    alignItems="center"
                    gridGap={8}
                    component="span"
                  >
                    {IconTipoCanal}
                    <Box component="span">{displayPhone}</Box>
                  </Box>
                </Box>
              }
              primaryTypographyProps={{
                component: 'div',
                noWrap: true,
              }}
              secondaryTypographyProps={{
                component: 'div',
                style: { fontSize: 11 },
              }}
            />
          </Box>
        </Box>
      </Box>

      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        variant="fullWidth"
        centered
        className={classes.tab}
      >
        <Tab label="Atendimento" onClick={() => setTabValue(0)} />
        {isSmallScreen && (
          <Tab label="Histórico" onClick={() => setTabValue(1)} />
        )}
        <Tab label="Arquivos" onClick={() => setTabValue(arquivosIndex)} />
      </Tabs>

      <Divider />

      <Box flex={1} style={{ minHeight: 0, overflow: 'hidden' }}>
        <TabPanel value={tabValue} index={0} className={classes.tabPanel}>
          <TabPanelAtendimento />
        </TabPanel>

        {isSmallScreen && (
          <TabPanel value={tabValue} index={1}>
            <Box height="100%" style={{ overflow: 'hidden' }}>
              <MessagesGridItem
                atendimento={atendimento}
                contato={contatoV2}
                initialLoadingMessages={initialLoadingMessages}
                onReset={onReset}
              />
            </Box>
          </TabPanel>
        )}

        <TabPanel
          value={tabValue}
          index={arquivosIndex}
          className={classes.tabPanel}
        >
          <TabPanelArquivos contatoId={contatoId} />
        </TabPanel>
      </Box>
    </Box>
  );
}
